import { FunctionPlugin, computed, inject } from 'vue'
import { RouteLocationNamedRaw, Router, useRoute } from 'vue-router'

import { mapLocaleRouteParamName, mapLocaleToHrefLang } from '@/i18n/helpers'
import { useI18nAddon } from '@/i18n/i18n'
import { AvailableLocale } from '@/i18n/types'
import { ROUTE_NAMES } from '@/router/constants'

import { useConfig } from './config'

const LOCALIZED_ROUTERS_PROVIDE_KEY = Symbol('localized-routers')

export const createConfigLocalizedRouters =
    (routers: Record<AvailableLocale, Router>): FunctionPlugin =>
    (app) => {
        app.provide(LOCALIZED_ROUTERS_PROVIDE_KEY, routers)
    }

type RouteMapper = (route: RouteLocationNamedRaw, locale: AvailableLocale) => RouteLocationNamedRaw
type MetaHrefLink = {
    rel: string
    href: string
    hreflang?: string
}

const removeRouteQuery = (x: RouteLocationNamedRaw): RouteLocationNamedRaw => ({ ...x, query: {} })

export function useLocalizedRouters() {
    const defaultLocale = useI18nAddon().getDefaultLocale()
    const getAppMapRoute = (map: RouteMapper) => (initialRoute: RouteLocationNamedRaw, locale: AvailableLocale) =>
        map(initialRoute, locale)

    const routers = inject<Record<AvailableLocale, Router>>(LOCALIZED_ROUTERS_PROVIDE_KEY)
    const { VUE_APP_HOST_URL } = useConfig()

    if (!routers) {
        throw new Error('No localized routers provided in Vue app.')
    }

    const currentRoute = useRoute()
    // turn RouteLocationNormalizedLoaded into RouteLocationNamedRaw
    const normalizedCurrentRoute = computed(
        (): RouteLocationNamedRaw =>
            currentRoute.name
                ? {
                      name: currentRoute.name || undefined,
                      params: currentRoute.params,
                      query: currentRoute.query,
                  }
                : {
                      name: ROUTE_NAMES.MAIN,
                  },
    )

    const getCanonicalAndAlternates = ({
        forRoute = normalizedCurrentRoute.value,
        mapRoute = removeRouteQuery,
        onlyCanonical,
    }: {
        forRoute?: RouteLocationNamedRaw
        mapRoute?: RouteMapper
        onlyCanonical?: boolean
    } = {}): MetaHrefLink[] => {
        const result: MetaHrefLink[] = []
        const defaultRouter = routers[defaultLocale]
        const forRouteCanonicalParams = { ...(forRoute.params || {}), locale: undefined }
        const canonicalWithoutLocaleParam = { ...forRoute, params: forRouteCanonicalParams }
        const onlyCanonicalRoute = defaultRouter.resolve(canonicalWithoutLocaleParam).meta.noLocaleInRoute
        if (onlyCanonicalRoute && onlyCanonical === false) {
            throw new Error('onlyCanonical mode cannot be turned off for routes with noLocaleInRoute meta')
        }
        if (onlyCanonicalRoute || onlyCanonical) {
            const forRouteMapped = mapRoute(canonicalWithoutLocaleParam, defaultLocale)
            result.push({
                rel: 'canonical',
                href: `${VUE_APP_HOST_URL}${defaultRouter.resolve(forRouteMapped).fullPath}`,
            })
            return result
        }
        const currentLocaleParam = normalizedCurrentRoute.value.params?.locale as string | undefined
        const currentLocale = currentLocaleParam ? mapLocaleRouteParamName(currentLocaleParam) : defaultLocale

        ;(Object.entries(routers) as Array<[AvailableLocale, Router]>).forEach(([locale, localizedRouter]) => {
            const { locale: _, ...forRouteParams } = forRoute.params || {}
            const forRouteMapped = mapRoute({ ...forRoute, params: forRouteParams }, locale)
            if (locale === currentLocale) {
                result.push({
                    rel: 'canonical',
                    href: `${VUE_APP_HOST_URL}${localizedRouter.resolve(forRouteMapped).fullPath}`,
                    hreflang: locale === currentLocale ? undefined : mapLocaleToHrefLang(locale),
                })
            }
            result.push({
                rel: 'alternate',
                href: `${VUE_APP_HOST_URL}${localizedRouter.resolve(forRouteMapped).fullPath}`,
                hreflang: mapLocaleToHrefLang(locale),
            })
            if (locale === defaultLocale) {
                result.push({
                    rel: 'alternate',
                    href: `${VUE_APP_HOST_URL}${localizedRouter.resolve(forRouteMapped).fullPath}`,
                    hreflang: 'x-default',
                })
            }
        })
        return result
    }

    const getLocalizedUrls = ({
        forRoute = normalizedCurrentRoute.value,
        mapRoute = removeRouteQuery,
    }: {
        forRoute?: RouteLocationNamedRaw
        mapRoute?: RouteMapper
    } = {}): Record<AvailableLocale, string> => {
        const appMapRoute = getAppMapRoute(mapRoute)

        const { locale: _, ...forRouteParams } = forRoute.params || {}

        const result: Record<AvailableLocale, string> = {} as Record<AvailableLocale, string>

        ;(Object.entries(routers) as Array<[AvailableLocale, Router]>).forEach(([locale, localizedRouter]) => {
            const forRouteMapped = appMapRoute({ ...forRoute, params: forRouteParams }, locale)
            result[locale] = localizedRouter.resolve(forRouteMapped).fullPath
        })

        return result
    }

    return { getCanonicalAndAlternates, getLocalizedUrls }
}
